/**
 * Browser Reveal Simulation Utilities
 *
 * Provides utilities for simulating Reveal data in testing environments.
 * Used by playground to test enrichment without requiring actual Reveal.js installation.
 */

import { roseStorage } from '../services/rose-storage';
import { logger } from '../utils/core/logger';
import type { BrowserRevealData } from './types';

/**
 * Check if Reveal simulation is enabled in Rose storage.
 */
function isRevealSimulationEnabled(): boolean {
    if (typeof window === 'undefined') {
        return false;
    }

    try {
        return roseStorage.getRevealSimulationEnabled();
    } catch (error) {
        logger.debug('[Reveal Simulation] Failed to check simulation status:', error);
        return false;
    }
}

/**
 * Check if RB2B (US visitor) simulation is enabled in Rose storage.
 */
function isRb2bSimulationEnabled(): boolean {
    if (typeof window === 'undefined') {
        return false;
    }

    try {
        return roseStorage.getRb2bSimulationEnabled();
    } catch (error) {
        logger.debug('[RB2B Simulation] Failed to check simulation status:', error);
        return false;
    }
}

/**
 * Get mock Reveal data for testing.
 */
function getMockRevealData(): BrowserRevealData {
    return {
        ip: '203.0.113.45',
        domain: 'example.com',
        type: 'company',
        company: 'Example Corporation',
        confidenceScore: 0.92,
        fuzzy: false,
        geoIP: {
            city: 'San Francisco',
            state: 'California',
            stateCode: 'CA',
            country: 'United States',
            countryCode: 'US',
        },
    };
}

/**
 * Get mock Reveal data for an RB2B person-identity test.
 *
 * The person-identity waterfall only consumes the visitor IP (+ user agent), so this
 * carries a real, routable US IP. Whether RB2B returns a person depends on RB2B's own
 * coverage for that IP — paste a US IP you know RB2B matches.
 */
export function getRb2bMockRevealData(ip: string): BrowserRevealData {
    return {
        ip,
        domain: null,
        type: 'isp',
        company: null,
        confidenceScore: null,
        fuzzy: false,
        geoIP: {
            country: 'United States',
            countryCode: 'US',
        },
    };
}

/**
 * Inject mock Reveal data if simulation is enabled.
 * Called during widget initialization.
 */
export function injectRevealSimulationIfEnabled(): void {
    // RB2B sim takes precedence: it tests the person-identity waterfall with a real US IP.
    if (isRb2bSimulationEnabled()) {
        const ip = roseStorage.getRb2bSimulationIp();
        if (!ip) {
            return;
        }
        try {
            const mockData = getRb2bMockRevealData(ip);
            (window as Window & { reveal?: BrowserRevealData }).reveal = mockData;
            logger.debug('[RB2B Simulation] Mock US visitor injected:', mockData);
        } catch (error) {
            logger.error('[RB2B Simulation] Failed to inject mock data:', error);
        }
        return;
    }

    if (!isRevealSimulationEnabled()) {
        return;
    }

    try {
        const mockData = getMockRevealData();
        (window as Window & { reveal?: BrowserRevealData }).reveal = mockData;
        logger.debug('[Reveal Simulation] Mock data injected:', mockData);
    } catch (error) {
        logger.error('[Reveal Simulation] Failed to inject mock data:', error);
    }
}
